/*
 * /treatment/$patientId (page)
 */
import {
	Badge,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Page,
	PageHeader,
	Placeholder,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import { TreatmentPlanForm } from "../../features/treatments/treatment-plan-form"
import { TIME_PERIOD_OPTIONS } from "../../features/treatments/treatment-plan-constants"
import type { FormProducer, FormProduct, ExistingTreatmentItem } from "../../features/treatments/treatment-plan-constants"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/treatment/$patientId")({
	component: TreatmentPage,
})

/* ─── Mock data (used as fallback when DB is empty) ─────────────────────── */

const MOCK_PRODUCERS: FormProducer[] = [
	{ id: "a1000000-0000-0000-0000-000000000001", name: "Tilray Medical", shortName: "TLR" },
	{ id: "a1000000-0000-0000-0000-000000000002", name: "Aurora Cannabis", shortName: "ACB" },
	{ id: "a1000000-0000-0000-0000-000000000003", name: "Canopy Growth", shortName: "CGC" },
	{ id: "a1000000-0000-0000-0000-000000000004", name: "Organigram", shortName: "OGI" },
]

const MOCK_PRODUCTS: FormProduct[] = [
	// Tilray
	{ id: "b1000000-0000-0000-0000-000000000001", producerId: "a1000000-0000-0000-0000-000000000001", name: "Tilray 1:1 CBD/THC Dried Flower" },
	{ id: "b1000000-0000-0000-0000-000000000002", producerId: "a1000000-0000-0000-0000-000000000001", name: "Tilray CBD Oil 25mg/mL" },
	{ id: "b1000000-0000-0000-0000-000000000003", producerId: "a1000000-0000-0000-0000-000000000001", name: "Tilray Indica Capsule 10mg" },
	// Aurora
	{ id: "b1000000-0000-0000-0000-000000000004", producerId: "a1000000-0000-0000-0000-000000000002", name: "Aurora Indica Dried Flower" },
	{ id: "b1000000-0000-0000-0000-000000000005", producerId: "a1000000-0000-0000-0000-000000000002", name: "Daily Special THC Oil 15mg/mL" },
	{ id: "b1000000-0000-0000-0000-000000000006", producerId: "a1000000-0000-0000-0000-000000000002", name: "Aurora Softgel CBD 25mg" },
	// Canopy
	{ id: "b1000000-0000-0000-0000-000000000007", producerId: "a1000000-0000-0000-0000-000000000003", name: "Tweed Bakerstreet (Sativa)" },
	{ id: "b1000000-0000-0000-0000-000000000008", producerId: "a1000000-0000-0000-0000-000000000003", name: "Spectrum Cannabis Red Oil" },
	// Organigram
	{ id: "b1000000-0000-0000-0000-000000000009", producerId: "a1000000-0000-0000-0000-000000000004", name: "Organigram Edison Jolts Mint" },
	{ id: "b1000000-0000-0000-0000-000000000010", producerId: "a1000000-0000-0000-0000-000000000004", name: "OrganiGram Relief CBD Tincture" },
]

/* ─── Page ───────────────────────────────────────────────────────────────── */

function TreatmentPage() {
	const { patientId } = Route.useParams()

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: treatmentPlan, isLoading: planLoading } =
		trpc.treatmentPlan.getByFilter.useQuery({
			where: { patientId, isLatest: true, active: true },
		})

	const { data: existingTreatments, isLoading: treatmentsLoading } =
		trpc.treatment.getAllByFilter.useQuery(
			{ where: { treatmentPlanId: treatmentPlan?.id, active: true } },
			{ enabled: !!treatmentPlan?.id },
		)

	const { data: allRecommendedProducts, isLoading: recProductsLoading } =
		trpc.recommendedProduct.getAllByFilter.useQuery(
			{ where: { patientId, active: true } },
			{ enabled: !!treatmentPlan?.id },
		)

	// Load producers and products from DB; fall back to mock data for testing
	const { data: dbProducers } = trpc.producer.getAllByFilter.useQuery({
		where: { active: true },
	})
	const { data: dbProducts } = trpc.product.getAllByFilter.useQuery({
		where: { active: true },
	})

	const producers: FormProducer[] =
		dbProducers && dbProducers.length > 0 ? dbProducers : MOCK_PRODUCERS
	const allProducts: FormProduct[] =
		dbProducts && dbProducts.length > 0 ? dbProducts : MOCK_PRODUCTS

	const isLoading =
		patientLoading ||
		planLoading ||
		(!!treatmentPlan?.id && (treatmentsLoading || recProductsLoading))

	// Memoized so TreatmentPlanForm's defaultValues don't reset on every render
	const existingTreatmentItems = useMemo<ExistingTreatmentItem[] | undefined>(
		() =>
			existingTreatments?.map((t) => {
				const recProducts = allRecommendedProducts?.filter((rp) => rp.treatmentId === t.id) ?? []
				return {
					timePeriodId: t.timePeriodId,
					dosingGuidelines: t.dosingGuidelines,
					products: recProducts.map((rp) => {
						const product = allProducts.find((p) => p.id === rp.productId)
						return {
							producerId: product?.producerId ?? "",
							productId: rp.productId,
							amount: rp.amount,
						}
					}),
				}
			}),
		[existingTreatments, allRecommendedProducts, allProducts],
	)

	const patientName = patient
		? `${patient.firstName} ${patient.lastName}`
		: null

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title={
						patientName
							? `Treatment Plan — ${patientName}`
							: "Treatment Plan"
					}
				/>

				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<>
						{/* Current plan summary */}
						{treatmentPlan && existingTreatments && existingTreatments.length > 0 && (
							<Card>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle>Current Treatment Plan</CardTitle>
											<CardDescription>
												Created{" "}
												{new Date(treatmentPlan.createdAt).toLocaleDateString()}
											</CardDescription>
										</div>
										<Badge variant="secondary">Active</Badge>
									</div>
								</CardHeader>
								<CardContent>
									<div className="flex flex-col gap-2">
										{[1, 2, 3, 4, 5].map((period) => {
											const periodTreatments = existingTreatments.filter(
												(t) => t.timePeriodId === period,
											)
											if (!periodTreatments.length) return null
											const label = TIME_PERIOD_OPTIONS.find(
												(o) => o.value === String(period),
											)?.label
											return (
												<div key={period}>
													<span className="text-sm font-medium">{label}: </span>
													{periodTreatments.map((t, i) => (
														<span
															key={i}
															className="text-sm text-muted-foreground"
														>
															{t.dosingGuidelines || "—"}
															{i < periodTreatments.length - 1 ? "; " : ""}
														</span>
													))}
												</div>
											)
										})}
										{treatmentPlan.notes && (
											<p className="text-sm text-muted-foreground mt-1">
												<span className="font-medium">Notes: </span>
												{treatmentPlan.notes}
											</p>
										)}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Form */}
						<Card>
							<CardHeader>
								<CardTitle>
									{treatmentPlan ? "Update Treatment Plan" : "New Treatment Plan"}
								</CardTitle>
								{treatmentPlan && (
									<CardDescription>
										Saving will create a new version and mark the current one as
										previous.
									</CardDescription>
								)}
							</CardHeader>
							<CardContent>
								<TreatmentPlanForm
									patientId={patientId}
									treatmentPlan={treatmentPlan ?? undefined}
									existingTreatmentItems={existingTreatmentItems}
									producers={producers}
									allProducts={allProducts}
								/>
							</CardContent>
						</Card>
					</>
				)}
			</Container>
		</Page>
	)
}
